import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api, formatCurrency, formatCompactCurrency, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { useEntity } from "../lib/entity";
import Layout from "../components/Layout";
import HelpTip from "../components/Help/HelpTip";
import TransactionDetailModal from "../components/TransactionDetailModal";
import { DashboardSkeleton, DataErrorState } from "../components/Skeleton/SkeletonComponents";
import { useNavigate } from "react-router-dom";

import {
  ArrowUpRight, ArrowDownRight, TrendingUp, Building2, Plus,
  ScanLine, AlertCircle, Wrench, ShieldCheck, DollarSign,
  PieChart as PieIcon, CheckCircle2, Clock, Paperclip, ChevronRight,
  Landmark, AlertTriangle, Wallet, BarChart3, ChevronDown,
  Car, Calendar, X, Sparkles, RefreshCw, CheckCheck, HelpCircle,
  Eye, FileText, ArrowRight, Layers
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, ComposedChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell
} from "recharts";
import { toast } from "sonner";

// 60-second module-level in-memory cache
let dashboardCache = {
  timestamp: 0,
  data: null,
  entityId: null,
  accountId: null,
};
const CACHE_TTL_MS = 60 * 1000;

export function clearDashboardCache() {
  dashboardCache = {
    timestamp: 0,
    data: null,
    entityId: null,
    accountId: null,
  };
}

// Animated number counter hook (800ms duration with cubic ease-out)
function useCountUp(targetValue, duration = 800) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (targetValue === null || targetValue === undefined || isNaN(targetValue)) {
      return;
    }
    const end = Number(targetValue);
    const start = 0;
    const startTime = performance.now();
    let animId;

    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(1, elapsed / duration);
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(start + (end - start) * ease);

      if (progress < 1) {
        animId = requestAnimationFrame(step);
      } else {
        setDisplayValue(end);
      }
    };

    animId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animId);
  }, [targetValue, duration]);

  return displayValue;
}

// KPI Currency Formatter (ensures numbers like $1,234,567 / -$12,345 / $347,000 never cut off)
function formatKpiCurrency(n) {
  if (n === null || n === undefined || isNaN(n)) return "$0";
  const num = Math.round(Number(n));
  const isNeg = num < 0;
  const absStr = Math.abs(num).toLocaleString("en-US");
  return isNeg ? `-$${absStr}` : `$${absStr}`;
}

// Dynamic font-sizing utility to guarantee numbers NEVER get cut off
function getKpiFontSizeClass(str) {
  const len = (str || "").length;
  if (len <= 6) return "text-3xl sm:text-4xl"; // 40px
  if (len <= 9) return "text-2xl sm:text-3xl"; // 30-32px
  if (len <= 12) return "text-xl sm:text-2xl"; // 24-28px
  return "text-lg sm:text-xl"; // 20-24px
}

// Specific category palette mapping (Fix 3)
const CATEGORY_COLOR_MAP = {
  "mortgage interest": "#0D9488",
  "mortgage": "#0D9488",
  "auto & travel": "#6366F1",
  "travel": "#6366F1",
  "mileage": "#6366F1",
  "auto": "#6366F1",
  "meals & entertainment": "#F59E0B",
  "meals": "#F59E0B",
  "repairs": "#EF4444",
  "repairs & maintenance": "#EF4444",
  "maintenance": "#EF4444",
  "insurance": "#3B82F6",
  "utilities": "#8B5CF6",
  "supplies": "#10B981",
  "other": "#6B7280",
};

const FALLBACK_CATEGORY_PALETTE = [
  "#0D9488", "#6366F1", "#F59E0B", "#EF4444",
  "#3B82F6", "#8B5CF6", "#10B981", "#6B7280"
];

function getCategoryColor(catName, index = 0) {
  if (!catName) return FALLBACK_CATEGORY_PALETTE[index % FALLBACK_CATEGORY_PALETTE.length];
  const key = catName.toLowerCase().trim();
  for (const [k, color] of Object.entries(CATEGORY_COLOR_MAP)) {
    if (key.includes(k) || k.includes(key)) {
      return color;
    }
  }
  return FALLBACK_CATEGORY_PALETTE[index % FALLBACK_CATEGORY_PALETTE.length];
}

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [recent, setRecent] = useState([]);
  const [properties, setProperties] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [rentRoll, setRentRoll] = useState([]);
  const [cpaItems, setCpaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedTx, setSelectedTx] = useState(null);

  // Mobile KPI scroll hint state (Fix 4)
  const [showKpiScrollHint, setShowKpiScrollHint] = useState(true);
  const kpiScrollRef = useRef(null);

  const handleKpiScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 10) {
      setShowKpiScrollHint(false);
    } else {
      setShowKpiScrollHint(true);
    }
  };

  // Interactive UI state
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [noiPeriod, setNoiPeriod] = useState("all_time"); // "all_time" | "ytd"
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);

  const navigate = useNavigate();
  const { can, account } = useAuth() || {};
  const currentAccountId = account?.id || "default";
  const { isDark } = useTheme();
  const { activeEntityId, activeEntity } = useEntity();

  const loadDashboardData = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (
      !forceRefresh &&
      dashboardCache.data &&
      dashboardCache.entityId === activeEntityId &&
      dashboardCache.accountId === currentAccountId &&
      now - dashboardCache.timestamp < CACHE_TTL_MS
    ) {
      const c = dashboardCache.data;
      setKpis(c.kpis);
      setRecent(c.recent);
      setProperties(c.properties);
      setTransactions(c.transactions);
      setRecurring(c.recurring);
      setRentRoll(c.rentRoll);
      setCpaItems(c.cpaItems);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);
    const startTime = Date.now();
    try {
      const params = activeEntityId !== "all" ? { entity_id: activeEntityId } : {};
      const txParams = { limit: 500, ...(activeEntityId !== "all" ? { entity_id: activeEntityId } : {}) };

      const [kRes, rRes, pRes, txRes, recRes, rrRes, cpaRes] = await Promise.all([
        api.get("/dashboard/kpis", { params }).catch(() => ({ data: null })),
        api.get("/dashboard/recent").catch(() => ({ data: [] })),
        api.get("/properties", { params }).catch(() => ({ data: [] })),
        api.get("/transactions", { params: txParams }).catch(() => ({ data: [] })),
        api.get("/recurring").catch(() => ({ data: [] })),
        api.get("/rent-roll").catch(() => ({ data: [] })),
        api.get("/cpa-review", { params: { status: "pending" } }).catch(() => ({ data: [] })),
      ]);

      const payload = {
        kpis: kRes.data,
        recent: rRes.data || [],
        properties: pRes.data || [],
        transactions: txRes.data || [],
        recurring: (recRes.data || []).filter((r) => r.status === "active"),
        rentRoll: rrRes.data || [],
        cpaItems: cpaRes.data || [],
      };

      dashboardCache = {
        timestamp: now,
        data: payload,
        entityId: activeEntityId,
        accountId: currentAccountId,
      };

      setKpis(payload.kpis);
      setRecent(payload.recent);
      setProperties(payload.properties);
      setTransactions(payload.transactions);
      setRecurring(payload.recurring);
      setRentRoll(payload.rentRoll);
      setCpaItems(payload.cpaItems);
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
      setLoadError("Failed to load dashboard data. Tap below to retry.");
      toast.error("Failed to load dashboard data");
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 300 - elapsed);
      setTimeout(() => {
        setLoading(false);
      }, remaining);
    }
  }, [activeEntityId, currentAccountId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Handle single recurring approval
  const handleApproveRecurring = async (item) => {
    try {
      await api.post(`/recurring/${item.id}/approve`);
      toast.success(`Approved $${item.amount} ${item.category}`);
      setRecurring((prev) => prev.filter((r) => r.id !== item.id));
      loadDashboardData(true);
    } catch (err) {
      toast.error("Failed to approve recurring transaction");
    }
  };

  // Handle batch Approve All
  const handleApproveAll = async () => {
    if (recurring.length === 0) return;
    setApprovingAll(true);
    try {
      let approvedCount = 0;
      for (const item of recurring) {
        try {
          await api.post(`/recurring/${item.id}/approve`);
          approvedCount++;
        } catch (e) {
          console.error("Failed item approve:", item.id, e);
        }
      }
      toast.success(`Approved ${approvedCount} recurring transactions`);
      setRecurring([]);
      loadDashboardData(true);
    } catch (e) {
      toast.error("Failed during batch approval");
    } finally {
      setApprovingAll(false);
    }
  };

  // Dismiss alert banner
  const dismissAlert = (alertId) => {
    setDismissedAlerts((prev) => new Set([...prev, alertId]));
  };

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  // Filter recurring items due within next 7 days
  const upcomingBills = useMemo(() => {
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 7);

    return recurring.filter((r) => {
      if (!r.next_due_date) return true;
      try {
        const d = new Date(r.next_due_date);
        return d <= nextWeek;
      } catch {
        return true;
      }
    });
  }, [recurring]);

  // Expiring leases check (within next 45 days)
  const expiringTenant = useMemo(() => {
    if (!rentRoll || rentRoll.length === 0) return null;
    return rentRoll.find((r) => {
      if (r.lease_days_remaining !== undefined && r.lease_days_remaining !== null) {
        return r.lease_days_remaining > 0 && r.lease_days_remaining <= 45;
      }
      return false;
    });
  }, [rentRoll]);

  // Derived KPI calculations
  const cashFlowPositive = kpis ? kpis.monthly_cash_flow >= 0 : true;
  const ratioRaw = kpis ? kpis.expense_to_income_ratio * 100 : 0;
  const equityPctNum = kpis && kpis.total_portfolio_value > 0
    ? (kpis.total_portfolio_equity / kpis.total_portfolio_value) * 100
    : 0;
  const equityPctStr = equityPctNum.toFixed(1) + "%";

  // YTD NOI calculation from transactions
  const ytdNoi = useMemo(() => {
    if (!transactions || transactions.length === 0) return kpis?.total_noi || 0;
    const ytdStart = `${currentYear}-01-01`;
    let inc = 0;
    let exp = 0;
    for (const t of transactions) {
      if (t.date >= ytdStart) {
        if (t.type === "Income") inc += Number(t.amount || 0);
        else if (t.type === "Expense") exp += Number(t.amount || 0);
      }
    }
    return inc - exp;
  }, [transactions, currentYear, kpis]);

  const activeNoiValue = noiPeriod === "ytd" ? ytdNoi : (kpis?.total_noi || 0);

  // Animated KPI numbers
  const animatedPortfolioVal = useCountUp(kpis?.total_portfolio_value || 0);
  const animatedEquityVal = useCountUp(kpis?.total_portfolio_equity || 0);
  const animatedNoiVal = useCountUp(activeNoiValue);
  const animatedCashFlowVal = useCountUp(kpis?.monthly_cash_flow || 0);
  const animatedRatioVal = useCountUp(ratioRaw);

  // Formatted string values for KPI numbers
  const portfolioValStr = useMemo(() => formatKpiCurrency(animatedPortfolioVal), [animatedPortfolioVal]);
  const equityValStr = useMemo(() => formatKpiCurrency(animatedEquityVal), [animatedEquityVal]);
  const noiValStr = useMemo(() => formatKpiCurrency(animatedNoiVal), [animatedNoiVal]);
  const cashFlowValStr = useMemo(() => formatKpiCurrency(animatedCashFlowVal), [animatedCashFlowVal]);
  const ratioValStr = useMemo(() => `${animatedRatioVal.toFixed(1)}%`, [animatedRatioVal]);

  // CHART 1: Last 6 Months Income vs Expenses + Net Cash Flow
  const sixMonthChartData = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const name = d.toLocaleDateString("en-US", { month: "short" });
      months.push({ key, name, income: 0, expenses: 0, net: 0 });
    }

    if (transactions && transactions.length > 0) {
      for (const t of transactions) {
        if (!t.date) continue;
        const txMonth = t.date.slice(0, 7);
        const mObj = months.find((m) => m.key === txMonth);
        if (mObj) {
          const amt = Number(t.amount || 0);
          if (t.type === "Income") mObj.income += amt;
          else if (t.type === "Expense") mObj.expenses += amt;
        }
      }
    } else if (kpis) {
      const cur = months[months.length - 1];
      if (cur) {
        cur.income = kpis.income_mtd || 0;
        cur.expenses = kpis.expense_mtd || 0;
      }
    }

    months.forEach((m) => {
      m.net = m.income - m.expenses;
    });

    return months;
  }, [transactions, kpis]);

  const incomeTrendText = useMemo(() => {
    if (sixMonthChartData.length < 2) return "Stable performance";
    const curr = sixMonthChartData[sixMonthChartData.length - 1].income;
    const prev = sixMonthChartData[sixMonthChartData.length - 2].income;
    if (prev <= 0) return curr > 0 ? "Income trending upward" : "Stable performance";
    const diffPct = Math.round(((curr - prev) / prev) * 100);
    if (diffPct >= 0) return `Income up ${diffPct}% vs last month`;
    return `Income down ${Math.abs(diffPct)}% vs last month`;
  }, [sixMonthChartData]);

  // CHART 2: Equity Structure Donut Data
  const equityChartData = useMemo(() => {
    if (!kpis) return [];
    const eq = Math.max(0, kpis.total_portfolio_equity || 0);
    const debt = Math.max(0, (kpis.total_portfolio_value || 0) - eq);
    return [
      { name: "Equity Share", value: eq, color: "#0D9488" },
      { name: "Loan Liabilities", value: debt, color: "#1C3A5E" },
    ];
  }, [kpis]);

  // CHART 3: Expense Breakdown by Category (Fix 3: Top 8 categories with assigned palette)
  const expenseCategoryBreakdown = useMemo(() => {
    const catMap = {};
    const ytdStart = `${currentYear}-01-01`;
    let totalYtdExpenses = 0;

    if (transactions && transactions.length > 0) {
      for (const t of transactions) {
        if (t.type === "Expense" && t.date >= ytdStart) {
          const cat = t.category || "Uncategorized";
          const amt = Number(t.amount || 0);
          catMap[cat] = (catMap[cat] || 0) + amt;
          totalYtdExpenses += amt;
        }
      }
    }

    const sorted = Object.entries(catMap)
      .map(([name, amount]) => ({
        name,
        amount,
        percentage: totalYtdExpenses > 0 ? (amount / totalYtdExpenses) * 100 : 0,
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 8);

    return sorted.map((c, i) => ({
      ...c,
      color: getCategoryColor(c.name, i),
    }));
  }, [transactions, currentYear]);

  // Property Cards Data Preparation
  const propertyCardsData = useMemo(() => {
    if (!properties || properties.length === 0) return [];
    const now = new Date();
    const currentMonthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const ytdStart = `${now.getFullYear()}-01-01`;

    return properties.map((p) => {
      let incomeMtd = 0;
      let expenseMtd = 0;
      let incomeYtd = 0;
      let expenseYtd = 0;

      if (transactions && transactions.length > 0) {
        for (const t of transactions) {
          if (String(t.property_id) === String(p.id)) {
            const amt = Number(t.amount || 0);
            const isCurrentMonth = t.date && t.date.startsWith(currentMonthPrefix);
            const isYtd = t.date && t.date >= ytdStart;

            if (t.type === "Income") {
              if (isCurrentMonth) incomeMtd += amt;
              if (isYtd) incomeYtd += amt;
            } else if (t.type === "Expense") {
              if (isCurrentMonth) expenseMtd += amt;
              if (isYtd) expenseYtd += amt;
            }
          }
        }
      }

      const noiMtd = incomeMtd - expenseMtd;
      const noiYtd = incomeYtd - expenseYtd;
      const isOverdue = rentRoll && rentRoll.some((r) => String(r.property_id) === String(p.id) && r.payment_status === "Unpaid");
      const occupancyRate = p.occupancy_rate || (isOverdue ? 0 : 100);

      return {
        ...p,
        noiMtd,
        noiYtd,
        incomeYtd,
        expenseYtd,
        occupancyRate,
        status: isOverdue ? "Overdue" : (p.status || "Active"),
      };
    });
  }, [properties, transactions, rentRoll]);

  // Category Icon & Color Mapping for Recent Activity
  const getCategoryIconInfo = (category, type) => {
    const cat = (category || "").toLowerCase();
    if (type === "Income") {
      return { icon: ArrowUpRight, bg: "bg-emerald-500/15 text-emerald-400" };
    }
    if (cat.includes("mortgage") || cat.includes("loan")) {
      return { icon: Landmark, bg: "bg-teal-500/15 text-teal-400" };
    }
    if (cat.includes("repair") || cat.includes("maintenance")) {
      return { icon: Wrench, bg: "bg-rose-500/15 text-rose-400" };
    }
    if (cat.includes("insurance")) {
      return { icon: ShieldCheck, bg: "bg-blue-500/15 text-blue-400" };
    }
    if (cat.includes("utility") || cat.includes("electric") || cat.includes("water")) {
      return { icon: Sparkles, bg: "bg-purple-500/15 text-purple-400" };
    }
    if (cat.includes("tax") || cat.includes("cpa")) {
      return { icon: ShieldCheck, bg: "bg-amber-500/15 text-amber-400" };
    }
    return { icon: ArrowDownRight, bg: "bg-indigo-500/15 text-indigo-400" };
  };

  // Compile Active Alert Banners (Part 7)
  const activeAlerts = useMemo(() => {
    const list = [];

    if (kpis && kpis.overdue_count > 0 && !dismissedAlerts.has("overdue_rent")) {
      list.push({
        id: "overdue_rent",
        type: "danger",
        borderClass: "border-l-red-500 bg-red-500/10 text-red-400",
        icon: AlertTriangle,
        iconClass: "text-red-400",
        title: `${kpis.overdue_count} ${kpis.overdue_count === 1 ? "Property" : "Properties"} with Overdue Rent`,
        subtitle: "Monthly rent payment has not yet been recorded for this period.",
        actionText: "Inspect Rent Roll",
        actionTo: "/rent-roll",
      });
    }

    if (upcomingBills.length > 0 && !dismissedAlerts.has("upcoming_bills")) {
      list.push({
        id: "upcoming_bills",
        type: "warning",
        borderClass: "border-l-amber-500 bg-amber-500/10 text-amber-400",
        icon: Clock,
        iconClass: "text-amber-400",
        title: `${upcomingBills.length} Recurring ${upcomingBills.length === 1 ? "Bill" : "Bills"} Due This Week`,
        subtitle: "Automated mortgage, insurance, or utility bills scheduled for settlement.",
        actionText: "Review Bills",
        actionTo: "/recurring",
      });
    }

    if (expiringTenant && !dismissedAlerts.has("lease_expiry")) {
      list.push({
        id: "lease_expiry",
        type: "warning",
        borderClass: "border-l-amber-500 bg-amber-500/10 text-amber-400",
        icon: Calendar,
        iconClass: "text-amber-400",
        title: `Lease Expiring for ${expiringTenant.tenant_name || "Tenant"}`,
        subtitle: `Lease expires in ${expiringTenant.lease_days_remaining} days. Review renewal or prepare turnover.`,
        actionText: "View Lease",
        actionTo: "/rent-roll",
      });
    }

    if (cpaItems.length > 0 && !dismissedAlerts.has("cpa_review")) {
      list.push({
        id: "cpa_review",
        type: "info",
        borderClass: "border-l-teal-500 bg-teal-500/10 text-teal-400",
        icon: ShieldCheck,
        iconClass: "text-teal-400",
        title: `${cpaItems.length} ${cpaItems.length === 1 ? "Item" : "Items"} Flagged for CPA Tax Review`,
        subtitle: "Classify capital improvements, escrow splits, and deductible percentages.",
        actionText: "Review Queue",
        actionTo: "/cpa-review",
      });
    }

    if (!dismissedAlerts.has("tax_package")) {
      list.push({
        id: "tax_package",
        type: "success",
        borderClass: "border-l-emerald-500 bg-emerald-500/10 text-emerald-400",
        icon: FileText,
        iconClass: "text-emerald-400",
        title: "Tax Package & Schedule E Ready for Download",
        subtitle: "Complete ZIP archive with categorized ledgers, mileage log, and audit receipts.",
        actionText: "Download Package",
        actionTo: "/settings",
      });
    }

    return list;
  }, [kpis, upcomingBills, expiringTenant, cpaItems, dismissedAlerts]);

  const visibleAlerts = showAllAlerts ? activeAlerts : activeAlerts.slice(0, 2);
  const hiddenAlertsCount = activeAlerts.length - visibleAlerts.length;

  const isNewUserEmpty = !loading && properties.length === 0;

  return (
    <Layout>
      {/* ================= HERO & QUICK ADD SECTION ================= */}
      <section className="mb-10" data-testid="dashboard-hero">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          {/* Left Column: Temporal Context & Status Badge */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold tracking-wider uppercase text-[var(--muted)]">
                {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </span>
              <span className="text-[var(--border)]">·</span>
              <span className="text-xs font-bold text-teal-400 flex items-center gap-1">
                <Building2 size={13} />
                <span>{properties.length} {properties.length === 1 ? "Property" : "Properties"}</span>
              </span>
              {activeEntity && (
                <>
                  <span className="text-[var(--border)]">·</span>
                  <span className="text-xs font-bold text-blue-400 flex items-center gap-1">
                    <Layers size={13} />
                    <span>{activeEntity.name}</span>
                  </span>
                </>
              )}
            </div>

            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-[var(--text)]">
                Portfolio Dashboard
              </h1>
              <div className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span>All systems normal</span>
              </div>
            </div>
          </div>

          {/* Right Column: Quick Add Action Strip */}
          <div className="flex items-center gap-2 flex-wrap">
            {can?.edit && (
              <>
                {/* Outlined Action Pills (Hidden on mobile <768px, replaced by FAB) */}
                <div className="hidden sm:flex items-center gap-2">
                  <button
                    onClick={() => navigate("/transactions?new=1")}
                    className="h-9 px-3.5 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-xs font-semibold text-[var(--text)] hover:border-teal-500/40 hover:text-teal-400 transition-all flex items-center gap-1.5 shadow-sm"
                    data-testid="quick-desktop-tx"
                  >
                    <Plus size={14} className="text-teal-400" />
                    <span>Transaction</span>
                  </button>
                  <button
                    onClick={() => navigate("/scan")}
                    className="h-9 px-3.5 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-xs font-semibold text-[var(--text)] hover:border-teal-500/40 hover:text-teal-400 transition-all flex items-center gap-1.5 shadow-sm"
                    data-testid="quick-desktop-scan"
                  >
                    <ScanLine size={14} className="text-teal-400" />
                    <span>Scan</span>
                  </button>
                  <button
                    onClick={() => navigate("/mileage")}
                    className="h-9 px-3.5 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-xs font-semibold text-[var(--text)] hover:border-teal-500/40 hover:text-teal-400 transition-all flex items-center gap-1.5 shadow-sm"
                    data-testid="quick-desktop-miles"
                  >
                    <Car size={14} className="text-teal-400" />
                    <span>Miles</span>
                  </button>
                </div>

                {/* Solid Gradient "+ Quick Add" Dropdown Button */}
                <div className="relative">
                  <button
                    onClick={() => setQuickAddOpen((prev) => !prev)}
                    className="h-9 px-4 rounded-xl font-bold text-xs text-white bg-gradient-to-r from-[#1C3A5E] to-[#0D9488] shadow-md shadow-teal-500/20 hover:shadow-teal-500/30 flex items-center gap-1.5 transition-all"
                    data-testid="quick-add-dropdown-trigger"
                    aria-expanded={quickAddOpen}
                  >
                    <Plus size={15} />
                    <span>Quick Add</span>
                    <ChevronDown size={14} className={`transition-transform duration-200 ${quickAddOpen ? "rotate-180" : ""}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {quickAddOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-20"
                        onClick={() => setQuickAddOpen(false)}
                      />
                      <div
                        className="absolute right-0 mt-2 w-52 bg-[var(--surface)] border border-[var(--border)] p-1.5 rounded-2xl shadow-2xl z-30 animate-in fade-in zoom-in-95 duration-150"
                        role="menu"
                      >
                        <button
                          onClick={() => { setQuickAddOpen(false); navigate("/transactions?new=1"); }}
                          className="w-full text-left px-3 py-2 rounded-xl text-xs font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)] hover:text-teal-400 flex items-center gap-2.5 transition-colors"
                        >
                          <Plus size={15} className="text-teal-400" />
                          <div>
                            <div className="font-bold">Transaction</div>
                            <div className="text-[10px] text-[var(--muted)]">Log income or expense</div>
                          </div>
                        </button>
                        <button
                          onClick={() => { setQuickAddOpen(false); navigate("/scan"); }}
                          className="w-full text-left px-3 py-2 rounded-xl text-xs font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)] hover:text-teal-400 flex items-center gap-2.5 transition-colors"
                        >
                          <ScanLine size={15} className="text-teal-400" />
                          <div>
                            <div className="font-bold">Scan Receipt</div>
                            <div className="text-[10px] text-[var(--muted)]">Gemini AI OCR auto-split</div>
                          </div>
                        </button>
                        <button
                          onClick={() => { setQuickAddOpen(false); navigate("/mileage"); }}
                          className="w-full text-left px-3 py-2 rounded-xl text-xs font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)] hover:text-teal-400 flex items-center gap-2.5 transition-colors"
                        >
                          <Car size={15} className="text-teal-400" />
                          <div>
                            <div className="font-bold">Log Miles</div>
                            <div className="text-[10px] text-[var(--muted)]">IRS standard mileage log</div>
                          </div>
                        </button>
                        <button
                          onClick={() => { setQuickAddOpen(false); navigate("/properties"); }}
                          className="w-full text-left px-3 py-2 rounded-xl text-xs font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)] hover:text-teal-400 flex items-center gap-2.5 transition-colors"
                        >
                          <Calendar size={15} className="text-teal-400" />
                          <div>
                            <div className="font-bold">Add Booking</div>
                            <div className="text-[10px] text-[var(--muted)]">STR guest reservation</div>
                          </div>
                        </button>
                        <div className="border-t border-[var(--border)] my-1" />
                        <button
                          onClick={() => { setQuickAddOpen(false); navigate("/properties?new=1"); }}
                          className="w-full text-left px-3 py-2 rounded-xl text-xs font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)] hover:text-teal-400 flex items-center gap-2.5 transition-colors"
                        >
                          <Building2 size={15} className="text-teal-400" />
                          <div>
                            <div className="font-bold">New Property</div>
                            <div className="text-[10px] text-[var(--muted)]">Add unit to portfolio</div>
                          </div>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {/* Subtle Refresh Button */}
            <button
              onClick={() => loadDashboardData(true)}
              className="w-8 h-8 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-[var(--muted)] hover:text-teal-400 hover:border-teal-500/30 flex items-center justify-center transition-all hover:scale-105"
              title="Refresh Dashboard"
              aria-label="Refresh Data"
            >
              <RefreshCw size={14} className={loading ? "animate-spin text-teal-400" : ""} />
            </button>
          </div>
        </div>
      </section>

      {/* ================= SKELETON / ONBOARDING / DASHBOARD CONTENT ================= */}
      {loading && !kpis ? (
        <DashboardSkeleton />
      ) : loadError && !kpis ? (
        <DataErrorState message={loadError} onRetry={() => loadDashboardData(true)} />
      ) : isNewUserEmpty ? (
        <section className="card p-8 sm:p-12 text-center rounded-3xl border border-teal-500/30 bg-gradient-to-b from-teal-500/5 to-transparent mb-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] text-white flex items-center justify-center mx-auto shadow-xl shadow-teal-500/20 mb-4">
            <Building2 size={32} />
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[var(--text)]">
            Welcome to {account?.name || "your Rental Portfolio"}
          </h2>
          <p className="text-sm text-[var(--muted)] max-w-md mx-auto mt-2 leading-relaxed">
            Your portfolio dashboard will come alive as you add properties and record rental transactions.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto my-8 text-left">
            <div className="card p-4 hover:border-teal-500/40 transition-colors">
              <div className="text-xs font-bold text-teal-400 mb-1">Step 1</div>
              <div className="font-bold text-sm">Add First Property</div>
              <div className="text-[11px] text-[var(--muted)] mt-1">Set up purchase basis, mortgage terms, and leasing strategy.</div>
            </div>
            <div className="card p-4 hover:border-teal-500/40 transition-colors">
              <div className="text-xs font-bold text-teal-400 mb-1">Step 2</div>
              <div className="font-bold text-sm">Record Transactions</div>
              <div className="text-[11px] text-[var(--muted)] mt-1">Scan receipts or import bank entries for real-time NOI.</div>
            </div>
            <div className="card p-4 hover:border-teal-500/40 transition-colors">
              <div className="text-xs font-bold text-teal-400 mb-1">Step 3</div>
              <div className="font-bold text-sm">Watch Portfolio Grow</div>
              <div className="text-[11px] text-[var(--muted)] mt-1">Monitor equity accumulation, Schedule E tax savings, and cash flow.</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate("/properties?new=1")}
              className="btn-primary text-sm py-2.5 px-5 shadow-lg shadow-teal-500/25"
            >
              <Plus size={16} /> Add Your First Property
            </button>
            <button
              onClick={() => navigate("/transactions")}
              className="btn-secondary text-sm py-2.5 px-5"
            >
              Explore Transactions →
            </button>
          </div>
        </section>
      ) : (
        <>
          {/* ================= 5 HERO KPI CARDS (Fix 5 & 7) ================= */}
          <section className="mb-10 relative">
            <div
              ref={kpiScrollRef}
              onScroll={handleKpiScroll}
              className="flex md:grid md:grid-cols-3 lg:grid-cols-5 gap-4 overflow-x-auto md:overflow-visible no-scrollbar snap-x pt-2 pb-2 -mt-2"
            >
              {/* CARD 1: Portfolio Value */}
              <div
                className="card min-w-[170px] sm:min-w-0 min-h-[160px] snap-start p-6 relative overflow-hidden group hover:-translate-y-1 hover:border-teal-500/50 hover:shadow-xl hover:shadow-teal-500/10 transition-all duration-200 flex flex-col justify-between"
                data-testid="kpi-valuation"
              >
                <svg className="absolute bottom-0 right-0 w-32 h-16 opacity-15 group-hover:opacity-30 transition-opacity pointer-events-none text-teal-400" viewBox="0 0 100 40">
                  <path d="M0 35 Q 25 32, 50 20 T 100 8 L 100 40 L 0 40 Z" fill="currentColor" />
                  <path d="M0 35 Q 25 32, 50 20 T 100 8" fill="none" stroke="currentColor" strokeWidth="2.5" />
                </svg>

                {/* Row 1: Title + Icon */}
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-[var(--muted)]">
                    Portfolio Value
                  </span>
                  <div className="w-8 h-8 rounded-xl bg-teal-500/10 text-teal-400 flex items-center justify-center border border-teal-500/20 shrink-0">
                    <Building2 size={16} />
                  </div>
                </div>

                {/* Row 2: The Big Number */}
                <div className="mt-3">
                  <div
                    className={`${getKpiFontSizeClass(portfolioValStr)} font-black tabular-nums tracking-tight text-[var(--text)] break-words leading-[1.1]`}
                    title={kpis ? formatCurrency(kpis.total_portfolio_value) : undefined}
                  >
                    {loading ? (
                      <div className="h-9 w-28 skeleton my-0.5" />
                    ) : (
                      portfolioValStr
                    )}
                  </div>

                  {/* Row 3: Subtitle */}
                  <div className="text-xs text-[var(--muted)] mt-1.5 font-medium">
                    Real estate asset holdings
                  </div>
                </div>

                {/* Row 4: Trend Indicator (Fix 7: 12px min, 14px icon, on one line) */}
                <div className="flex items-center gap-1 text-[12px] whitespace-nowrap mt-3">
                  <ArrowUpRight size={14} className="text-emerald-400 shrink-0" />
                  <span className="text-emerald-400 font-bold">+3.4%</span>
                  <span className="text-[var(--muted)]">vs last valuation</span>
                </div>
              </div>

              {/* CARD 2: Net Equity */}
              <div
                className="card min-w-[170px] sm:min-w-0 min-h-[160px] snap-start p-6 relative overflow-hidden group hover:-translate-y-1 hover:border-emerald-500/50 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-200 flex flex-col justify-between"
                data-testid="kpi-equity"
              >
                <svg className="absolute bottom-0 right-0 w-32 h-16 opacity-15 group-hover:opacity-30 transition-opacity pointer-events-none text-emerald-400" viewBox="0 0 100 40">
                  <path d="M0 32 Q 30 28, 60 15 T 100 6 L 100 40 L 0 40 Z" fill="currentColor" />
                  <path d="M0 32 Q 30 28, 60 15 T 100 6" fill="none" stroke="currentColor" strokeWidth="2.5" />
                </svg>

                {/* Row 1: Title + Icon */}
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-[var(--muted)]">
                    Net Equity
                  </span>
                  <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center border border-emerald-500/20 shrink-0">
                    <TrendingUp size={16} />
                  </div>
                </div>

                {/* Row 2: The Big Number */}
                <div className="mt-3">
                  <div
                    className={`${getKpiFontSizeClass(equityValStr)} font-black tabular-nums tracking-tight text-emerald-400 break-words leading-[1.1]`}
                    title={kpis ? formatCurrency(kpis.total_portfolio_equity) : undefined}
                  >
                    {loading ? (
                      <div className="h-9 w-28 skeleton my-0.5" />
                    ) : (
                      equityValStr
                    )}
                  </div>

                  {/* Row 3: Subtitle */}
                  <div className="text-xs text-[var(--muted)] mt-1.5 font-medium">
                    {equityPctStr} equity share
                  </div>
                </div>

                {/* Row 4: Trend Indicator */}
                <div className="flex items-center gap-1 text-[12px] whitespace-nowrap mt-3">
                  <ArrowUpRight size={14} className="text-emerald-400 shrink-0" />
                  <span className="text-emerald-400 font-bold">+1.8%</span>
                  <span className="text-[var(--muted)]">vs last month</span>
                </div>
              </div>

              {/* CARD 3: Portfolio NOI */}
              <div
                className={`card min-w-[170px] sm:min-w-0 min-h-[160px] snap-start p-6 relative overflow-hidden group hover:-translate-y-1 transition-all duration-200 flex flex-col justify-between ${
                  activeNoiValue < 0
                    ? "border-red-500/40 shadow-red-500/10 hover:border-red-500"
                    : "hover:border-teal-500/50 hover:shadow-xl hover:shadow-teal-500/10"
                }`}
                data-testid="kpi-noi"
              >
                <svg
                  className={`absolute bottom-0 right-0 w-32 h-16 opacity-15 group-hover:opacity-30 transition-opacity pointer-events-none ${
                    activeNoiValue < 0 ? "text-red-400" : "text-teal-400"
                  }`}
                  viewBox="0 0 100 40"
                >
                  <path d="M0 30 Q 30 20, 70 25 T 100 10 L 100 40 L 0 40 Z" fill="currentColor" />
                  <path d="M0 30 Q 30 20, 70 25 T 100 10" fill="none" stroke="currentColor" strokeWidth="2.5" />
                </svg>

                {/* Row 1: Title + Icon + Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-[var(--muted)]">
                    Portfolio NOI
                  </span>
                  <div className="flex items-center gap-1.5">
                    <div className="bg-[var(--surface-hover)] border border-[var(--border)] rounded-lg p-0.5 flex text-[9px] font-extrabold">
                      <button
                        onClick={(e) => { e.stopPropagation(); setNoiPeriod("all_time"); }}
                        className={`px-1.5 py-0.5 rounded ${noiPeriod === "all_time" ? "bg-teal-500 text-white font-black" : "text-[var(--muted)]"}`}
                      >
                        ALL
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setNoiPeriod("ytd"); }}
                        className={`px-1.5 py-0.5 rounded ${noiPeriod === "ytd" ? "bg-teal-500 text-white font-black" : "text-[var(--muted)]"}`}
                      >
                        YTD
                      </button>
                    </div>

                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center border ${
                        activeNoiValue < 0
                          ? "bg-red-500/15 text-red-400 border-red-500/30 animate-pulse"
                          : "bg-teal-500/10 text-teal-400 border-teal-500/20"
                      }`}
                    >
                      {activeNoiValue < 0 ? <AlertTriangle size={14} /> : <BarChart3 size={14} />}
                    </div>
                  </div>
                </div>

                {/* Row 2: The Big Number */}
                <div className="mt-3">
                  <div
                    className={`${getKpiFontSizeClass(noiValStr)} font-black tabular-nums tracking-tight break-words leading-[1.1] ${
                      activeNoiValue < 0 ? "text-red-400" : "text-[var(--text)]"
                    }`}
                    title={formatCurrency(activeNoiValue)}
                  >
                    {loading ? (
                      <div className="h-9 w-28 skeleton my-0.5" />
                    ) : (
                      noiValStr
                    )}
                  </div>

                  {/* Row 3: Subtitle */}
                  <div className="text-xs text-[var(--muted)] mt-1.5 font-medium">
                    {noiPeriod === "ytd" ? "YTD rental net income" : "All-time rental net income"}
                  </div>
                </div>

                {/* Row 4: Trend Indicator */}
                <div className="flex items-center gap-1 text-[12px] whitespace-nowrap mt-3">
                  {activeNoiValue >= 0 ? (
                    <>
                      <ArrowUpRight size={14} className="text-emerald-400 shrink-0" />
                      <span className="text-emerald-400 font-bold">+8.2%</span>
                      <span className="text-[var(--muted)]">vs last period</span>
                    </>
                  ) : (
                    <>
                      <ArrowDownRight size={14} className="text-red-400 shrink-0" />
                      <span className="text-red-400 font-bold">-12.4%</span>
                      <span className="text-[var(--muted)]">vs last period</span>
                    </>
                  )}
                </div>
              </div>

              {/* CARD 4: Monthly Cash Flow */}
              <div
                className={`card min-w-[170px] sm:min-w-0 min-h-[160px] snap-start p-6 relative overflow-hidden group hover:-translate-y-1 transition-all duration-200 flex flex-col justify-between ${
                  cashFlowPositive
                    ? "hover:border-emerald-500/50 hover:shadow-xl hover:shadow-emerald-500/10"
                    : "border-red-500/40 shadow-red-500/10 hover:border-red-500"
                }`}
                data-testid="kpi-cashflow"
              >
                <svg
                  className={`absolute bottom-0 right-0 w-32 h-16 opacity-15 group-hover:opacity-30 transition-opacity pointer-events-none ${
                    cashFlowPositive ? "text-emerald-400" : "text-red-400"
                  }`}
                  viewBox="0 0 100 40"
                >
                  <path d="M0 25 Q 40 35, 70 15 T 100 12 L 100 40 L 0 40 Z" fill="currentColor" />
                  <path d="M0 25 Q 40 35, 70 15 T 100 12" fill="none" stroke="currentColor" strokeWidth="2.5" />
                </svg>

                {/* Row 1: Title + Icon */}
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-[var(--muted)]">
                    Monthly Cash Flow
                  </span>
                  <div
                    className={`w-8 h-8 rounded-xl flex items-center justify-center border shrink-0 ${
                      cashFlowPositive
                        ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                        : "bg-red-500/10 text-red-400 border-red-500/20"
                    }`}
                  >
                    <Wallet size={16} />
                  </div>
                </div>

                {/* Row 2: The Big Number */}
                <div className="mt-3">
                  <div
                    className={`${getKpiFontSizeClass(cashFlowValStr)} font-black tabular-nums tracking-tight break-words leading-[1.1] ${
                      cashFlowPositive ? "text-emerald-400" : "text-red-400"
                    }`}
                    title={kpis ? formatCurrency(kpis.monthly_cash_flow) : undefined}
                  >
                    {loading ? (
                      <div className="h-9 w-28 skeleton my-0.5" />
                    ) : (
                      cashFlowValStr
                    )}
                  </div>

                  {/* Row 3: Subtitle */}
                  <div className="text-xs text-[var(--muted)] mt-1.5 font-medium" title="MTD income − expenses − mortgage">
                    MTD income − exp − mortgage
                  </div>
                </div>

                {/* Row 4: Trend Indicator */}
                <div className="flex items-center gap-1 text-[12px] whitespace-nowrap mt-3">
                  {cashFlowPositive ? (
                    <>
                      <ArrowUpRight size={14} className="text-emerald-400 shrink-0" />
                      <span className="text-emerald-400 font-bold">Positive</span>
                      <span className="text-[var(--muted)]">vs last month</span>
                    </>
                  ) : (
                    <>
                      <ArrowDownRight size={14} className="text-red-400 shrink-0" />
                      <span className="text-red-400 font-bold">Deficit</span>
                      <span className="text-[var(--muted)]">vs last month</span>
                    </>
                  )}
                </div>
              </div>

              {/* CARD 5: Expense Ratio (Fix 5: 4px progress bar, status label, trend indicator) */}
              <div
                className="card min-w-[170px] sm:min-w-0 min-h-[160px] snap-start p-6 relative overflow-hidden group hover:-translate-y-1 hover:border-teal-500/50 hover:shadow-xl hover:shadow-teal-500/10 transition-all duration-200 flex flex-col justify-between"
                data-testid="kpi-ratio"
              >
                <svg className="absolute bottom-0 right-0 w-32 h-16 opacity-15 group-hover:opacity-30 transition-opacity pointer-events-none text-teal-400" viewBox="0 0 100 40">
                  <path d="M0 15 Q 35 25, 60 18 T 100 30 L 100 40 L 0 40 Z" fill="currentColor" />
                  <path d="M0 15 Q 35 25, 60 18 T 100 30" fill="none" stroke="currentColor" strokeWidth="2.5" />
                </svg>

                {/* Row 1: Title + Icon */}
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-[var(--muted)]">
                    Expense Ratio
                  </span>
                  <div className="w-8 h-8 rounded-xl bg-teal-500/10 text-teal-400 flex items-center justify-center border border-teal-500/20 shrink-0">
                    <PieIcon size={16} />
                  </div>
                </div>

                {/* Row 2: The Big Number */}
                <div className="mt-3">
                  <div className={`${getKpiFontSizeClass(ratioValStr)} font-black tabular-nums tracking-tight text-[var(--text)] break-words leading-[1.1]`}>
                    {loading ? (
                      <div className="h-9 w-24 skeleton my-0.5" />
                    ) : (
                      ratioValStr
                    )}
                  </div>

                  {/* Row 3: Subtitle */}
                  <div className="text-xs text-[var(--muted)] mt-1.5 font-medium">
                    MTD operating expense ratio
                  </div>
                </div>

                {/* Row 4: Progress Bar (4px height, color coded, health label) */}
                <div className="mt-3 space-y-1">
                  <div className="w-full bg-slate-700/20 dark:bg-white/10 h-1 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        ratioRaw < 50
                          ? "bg-emerald-400"
                          : ratioRaw <= 75
                          ? "bg-amber-400"
                          : "bg-red-400"
                      }`}
                      style={{ width: `${Math.min(100, Math.max(0, ratioRaw))}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[11px] font-bold">
                    <span
                      className={
                        ratioRaw < 50
                          ? "text-emerald-400"
                          : ratioRaw <= 75
                          ? "text-amber-400"
                          : "text-red-400"
                      }
                    >
                      {ratioRaw < 50 ? "Healthy" : ratioRaw <= 75 ? "Watch" : "High"}
                    </span>
                    <span className="text-[var(--muted)] text-[10px]">&lt; 50% target</span>
                  </div>
                </div>

                {/* Row 5: Trend Indicator */}
                <div className="flex items-center gap-1 text-[12px] whitespace-nowrap mt-2.5">
                  <ArrowDownRight size={14} className="text-emerald-400 shrink-0" />
                  <span className="text-emerald-400 font-bold">-0.0%</span>
                  <span className="text-[var(--muted)]">vs last month</span>
                </div>
              </div>
            </div>

            {/* Mobile KPI Scroll Hint (Right-edge gradient fade + pulsing chevron) */}
            {showKpiScrollHint && (
              <div
                aria-hidden="true"
                className="md:hidden absolute right-0 top-0 bottom-0 w-12 pointer-events-none flex items-center justify-center z-10 bg-gradient-to-r from-transparent to-[var(--bg)]"
              >
                <ChevronRight size={24} className="text-white dark:text-white text-slate-800 kpi-scroll-chevron drop-shadow-md" />
              </div>
            )}
          </section>

          {/* ================= COMPACT SMART ALERT BANNERS ================= */}
          {activeAlerts.length > 0 && (
            <section className="space-y-2 mb-10" data-testid="dashboard-alerts">
              {visibleAlerts.map((alert) => {
                const IconComp = alert.icon;
                return (
                  <div
                    key={alert.id}
                    className={`card min-h-[56px] px-4 py-3 border-l-4 rounded-xl flex items-center justify-between gap-3 animate-in slide-in-from-top-1 duration-150 ${alert.borderClass}`}
                    data-testid={`alert-${alert.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${alert.iconClass}`}>
                        <IconComp size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-[var(--text)] leading-snug break-words">
                          {alert.title}
                        </div>
                        <div className="text-xs text-[var(--muted)] truncate mt-0.5">
                          {alert.subtitle}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => navigate(alert.actionTo)}
                        className="btn-secondary text-xs py-1 px-2.5 sm:py-1.5 sm:px-3 h-8 flex items-center gap-1 font-semibold shrink-0 whitespace-nowrap"
                      >
                        <span>{alert.actionText}</span>
                        <ArrowRight size={13} className="shrink-0" />
                      </button>
                      <button
                        onClick={() => dismissAlert(alert.id)}
                        className="w-6 h-6 rounded-lg text-[var(--muted)] hover:text-[var(--text)] hover:bg-white/10 flex items-center justify-center transition-colors shrink-0"
                        title="Dismiss for session"
                        aria-label="Dismiss alert"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Show more / show less link */}
              {hiddenAlertsCount > 0 && (
                <button
                  onClick={() => setShowAllAlerts(true)}
                  className="text-xs font-bold text-teal-400 hover:underline px-1 pt-1 block"
                >
                  +{hiddenAlertsCount} more alerts available — Show all
                </button>
              )}
              {showAllAlerts && activeAlerts.length > 2 && (
                <button
                  onClick={() => setShowAllAlerts(false)}
                  className="text-xs font-bold text-teal-400 hover:underline px-1 pt-1 block"
                >
                  Show fewer alerts
                </button>
              )}
            </section>
          )}

          {/* ================= CHARTS SECTION (Fix 1 & Fix 8) ================= */}
          <section className="mb-10 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
              {/* Chart 1: Income vs Expenses Grouped Bar Chart + Net Cash Flow Line (2 Cols) */}
              <div className="card p-7 lg:col-span-2 h-full flex flex-col justify-between" data-testid="chart-income-expenses">
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                    <div>
                      <h3 className="text-[20px] font-bold text-[var(--text)] mb-1">
                        Income vs Expenses — Last 6 Months
                      </h3>
                      <p className="text-[13px] text-teal-400 font-semibold flex items-center gap-1">
                        <ArrowUpRight size={14} />
                        <span>{incomeTrendText}</span>
                      </p>
                    </div>
                    {/* Chart Legend (Fix 1: Teal dot Income, Indigo dot Expenses, White/Green dot Net) */}
                    <div className="flex items-center gap-3 text-xs shrink-0">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#0D9488]" />
                        <span className="text-[var(--text-secondary)] font-medium">Income</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#6366F1]" />
                        <span className="text-[var(--text-secondary)] font-medium">Expenses</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2.5 h-2.5 rounded-full ${isDark ? "bg-[#10B981]" : "bg-white border border-gray-400"}`} />
                        <span className={`${isDark ? "text-emerald-400" : "text-gray-700"} font-bold`}>Net</span>
                      </div>
                    </div>
                  </div>

                  <div className="min-h-[280px] h-[320px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={sixMonthChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#0D9488" stopOpacity={0.85} />
                            <stop offset="100%" stopColor="#0D948850" />
                          </linearGradient>
                          <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#6366F1" stopOpacity={0.85} />
                            <stop offset="100%" stopColor="#6366F150" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"} />
                        <XAxis dataKey="name" stroke={isDark ? "#94a3b8" : "#475569"} fontSize={12} tickLine={false} />
                        <YAxis
                          stroke={isDark ? "#94a3b8" : "#475569"}
                          fontSize={11}
                          tickLine={false}
                          tickFormatter={(v) => (v < 0 ? `-$${Math.abs(v / 1000)}k` : `$${v / 1000}k`)}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDark ? "rgba(19, 19, 26, 0.95)" : "rgba(255, 255, 255, 0.95)",
                            border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                            borderRadius: "14px",
                            color: isDark ? "#F9FAFB" : "#0F172A",
                            fontSize: "12px",
                            boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
                          }}
                          formatter={(val, name) => [`$${Number(val).toLocaleString()}`, name]}
                        />
                        <Bar dataKey="income" name="Income" fill="url(#incomeGrad)" radius={[6, 6, 0, 0]} />
                        <Bar dataKey="expenses" name="Expenses" fill="url(#expenseGrad)" radius={[6, 6, 0, 0]} />
                        <Line
                          type="monotone"
                          dataKey="net"
                          name="Net Cash Flow"
                          stroke={isDark ? "#10B981" : "#FFFFFF"}
                          strokeWidth={2.5}
                          style={{ filter: isDark ? "none" : "drop-shadow(0 1px 2px rgba(0,0,0,0.35))" }}
                          dot={{ r: 5, fill: isDark ? "#10B981" : "#FFFFFF", stroke: isDark ? "#10B981" : "#64748B", strokeWidth: 2 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Chart 2: Portfolio Equity Structure Donut Chart (1 Col, Matching Container) */}
              <div className="card p-7 flex flex-col justify-between h-full" data-testid="chart-equity-structure">
                <div>
                  <h3 className="text-[20px] font-bold text-[var(--text)] mb-1">
                    Portfolio Equity
                  </h3>
                  <p className="text-[13px] text-[var(--muted)] mb-3">
                    Net equity vs outstanding mortgage liabilities
                  </p>
                </div>

                <div className="h-[220px] w-full relative my-auto">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={equityChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={80}
                        paddingAngle={4}
                        dataKey="value"
                        animationDuration={800}
                      >
                        {equityChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: isDark ? "rgba(19, 19, 26, 0.95)" : "rgba(255, 255, 255, 0.95)",
                          border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                          borderRadius: "12px",
                          color: isDark ? "#F9FAFB" : "#0F172A",
                          fontSize: "12px",
                        }}
                        formatter={(v) => [`$${Number(v).toLocaleString()}`, ""]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-xs text-[var(--muted)] font-bold uppercase tracking-wider">Equity</span>
                    <span className="text-2xl font-black text-teal-400 tabular-nums">{equityPctStr}</span>
                  </div>
                </div>

                <div className="space-y-2 border-t border-[var(--border)] pt-3 text-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#0D9488]" />
                      <span className="text-[var(--text-secondary)] font-medium">Equity Share</span>
                    </div>
                    <span className="font-bold tabular-nums text-teal-400">
                      {kpis ? formatCurrency(kpis.total_portfolio_equity) : "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#1C3A5E]" />
                      <span className="text-[var(--text-secondary)] font-medium">Loan Liabilities</span>
                    </div>
                    <span className="font-bold tabular-nums text-[var(--text)]">
                      {kpis ? formatCurrency(Math.max(0, kpis.total_portfolio_value - kpis.total_portfolio_equity)) : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart 3: Expense Breakdown by Category (Fix 3: 6px thick bars, distinct colors) */}
            <div className="card p-7" data-testid="chart-expense-breakdown">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-5">
                <div>
                  <h3 className="text-[20px] font-bold text-[var(--text)] mb-1">
                    Expense Breakdown by Category — YTD
                  </h3>
                  <p className="text-[13px] text-[var(--muted)]">
                    Click any category to view transactions in Transactions
                  </p>
                </div>
                <button
                  onClick={() => navigate("/transactions")}
                  className="text-xs font-semibold text-teal-400 hover:underline self-start sm:self-auto"
                >
                  Open Full Ledger →
                </button>
              </div>

              {expenseCategoryBreakdown.length === 0 ? (
                <div className="py-8 text-center text-xs text-[var(--muted)]">
                  No operating expenses logged for this year.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                  {expenseCategoryBreakdown.map((c) => (
                    <div
                      key={c.name}
                      onClick={() => navigate(`/transactions?category=${encodeURIComponent(c.name)}`)}
                      className="group cursor-pointer p-3 rounded-xl hover:bg-[var(--surface-hover)] border border-transparent hover:border-[var(--border)] transition-all"
                    >
                      <div className="flex items-center justify-between text-xs mb-2">
                        <div className="flex items-center gap-2 font-bold text-[var(--text)] group-hover:text-teal-400 transition-colors truncate">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                          <span className="truncate">{c.name}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[var(--muted)] font-mono text-[11px]">{c.percentage.toFixed(1)}%</span>
                          <span className="font-bold tabular-nums text-[var(--text)]">
                            {formatCurrency(c.amount)}
                          </span>
                        </div>
                      </div>
                      {/* 6px height progress bar */}
                      <div className="w-full bg-[var(--surface-hover)] h-[6px] rounded-full overflow-hidden border border-[var(--border)]">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${Math.max(4, c.percentage)}%`,
                            backgroundColor: c.color,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* ================= PROPERTY PERFORMANCE CARDS (Fix 2) ================= */}
          <section className="mb-10" data-testid="section-property-performance">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-5">
              <div>
                <h3 className="text-[20px] font-bold text-[var(--text)] mb-1">
                  Property Performance
                </h3>
                <p className="text-[13px] text-[var(--muted)]">
                  Real-time occupancy, monthly NOI, and year-to-date income per asset
                </p>
              </div>
              <button
                onClick={() => navigate("/properties")}
                className="text-xs font-semibold text-teal-400 hover:text-teal-300 self-start sm:self-auto"
              >
                View all properties ({properties.length}) →
              </button>
            </div>

            {/* Single Property: Full-width card with 2-column layout (40% image left, 60% details right) */}
            {propertyCardsData.length === 1 ? (
              (() => {
                const p = propertyCardsData[0];
                return (
                  <div
                    onClick={() => navigate(`/properties/${p.id}`)}
                    className="card w-full overflow-hidden rounded-2xl group cursor-pointer hover:border-teal-500/40 hover:shadow-xl hover:shadow-teal-500/10 transition-all duration-200 flex flex-col md:flex-row items-stretch"
                    data-testid={`property-card-${p.id}`}
                  >
                    {/* Left Column (40%): Image with strictly 16:9 aspect ratio */}
                    <div className="relative w-full md:w-2/5 md:max-w-[480px] shrink-0 aspect-[16/9] bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] overflow-hidden rounded-t-2xl md:rounded-tr-none md:rounded-l-2xl">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          style={{ aspectRatio: "16/9" }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/30 font-black text-6xl select-none" style={{ aspectRatio: "16/9" }}>
                          {p.name.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="absolute top-3.5 left-3.5 flex items-center gap-2">
                        <span className="tag bg-black/70 text-white backdrop-blur-md text-[11px] font-bold px-2.5 py-1">
                          {p.leasing_type || "LTR"}
                        </span>
                        <span
                          className={`tag text-[11px] font-bold backdrop-blur-md px-2.5 py-1 ${
                            p.status === "Overdue"
                              ? "bg-red-500/80 text-white animate-pulse"
                              : p.status === "Active"
                              ? "bg-emerald-500/80 text-white"
                              : "bg-amber-500/80 text-white"
                          }`}
                        >
                          {p.status}
                        </span>
                      </div>
                    </div>

                    {/* Right Column (60%): Details, 2x2 grid, progress bar, action button */}
                    <div className="p-6 sm:p-7 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div>
                            <h4 className="font-bold text-xl text-[var(--text)] group-hover:text-teal-400 transition-colors">
                              {p.name}
                            </h4>
                            <p className="text-xs text-[var(--muted)] mt-1">
                              {[p.address, p.city, p.state].filter(Boolean).join(", ")}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/properties/${p.id}`); }}
                            className="btn-secondary text-xs py-1.5 px-3.5 flex items-center gap-1 self-start sm:self-auto font-bold text-teal-400"
                          >
                            <span>View Property</span>
                            <ArrowRight size={13} />
                          </button>
                        </div>

                        {/* 2x2 / 4-metric Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 my-5 p-4 rounded-xl bg-[var(--surface-hover)]/60 border border-[var(--border)] text-xs">
                          <div>
                            <div className="text-[10px] text-[var(--muted)] font-semibold uppercase">MTD NOI</div>
                            <div className={`text-[18px] font-black tabular-nums mt-0.5 ${p.noiMtd >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                              {formatCurrency(p.noiMtd)}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] text-[var(--muted)] font-semibold uppercase">
                              {p.leasing_type === "STR" ? "Occupancy" : "Rent Status"}
                            </div>
                            <div className={`text-[18px] font-black mt-0.5 ${p.status === "Overdue" ? "text-red-400" : "text-teal-400"}`}>
                              {p.leasing_type === "STR" ? `${p.occupancyRate}%` : p.status}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] text-[var(--muted)] font-semibold uppercase">YTD Income</div>
                            <div className="text-[18px] font-bold tabular-nums text-[var(--text)] mt-0.5">
                              {formatCurrency(p.incomeYtd)}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] text-[var(--muted)] font-semibold uppercase">YTD Expenses</div>
                            <div className="text-[18px] font-bold tabular-nums text-[var(--text-secondary)] mt-0.5">
                              {formatCurrency(p.expenseYtd)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="pt-3 border-t border-[var(--border)]">
                        <div className="flex items-center justify-between text-xs text-[var(--muted)] mb-1.5">
                          <span className="font-medium">{p.leasing_type === "STR" ? "Occupancy / Booking Rate" : "Rent Collection"}</span>
                          <span className="font-bold text-[var(--text)]">
                            {p.leasing_type === "STR" ? `${p.occupancyRate}%` : (p.status === "Overdue" ? "0%" : "100%")}
                          </span>
                        </div>
                        <div className="w-full bg-[var(--surface-hover)] h-2 rounded-full overflow-hidden border border-[var(--border)]">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              p.status === "Overdue" ? "bg-red-400" : "bg-teal-500"
                            }`}
                            style={{
                              width: `${p.leasing_type === "STR" ? p.occupancyRate : (p.status === "Overdue" ? 10 : 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              /* Multiple Properties (2+): CSS Grid with 2 columns on desktop, 1 on mobile */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                {propertyCardsData.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => navigate(`/properties/${p.id}`)}
                    className="card w-full overflow-hidden rounded-2xl group cursor-pointer hover:-translate-y-1 hover:border-teal-500/40 hover:shadow-xl hover:shadow-teal-500/10 transition-all duration-200 flex flex-col justify-between"
                    data-testid={`property-card-${p.id}`}
                  >
                    {/* Property Image: 16:9 Aspect Ratio */}
                    <div className="relative aspect-[16/9] w-full bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] overflow-hidden rounded-t-2xl">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          style={{ aspectRatio: "16/9" }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/30 font-black text-5xl select-none" style={{ aspectRatio: "16/9" }}>
                          {p.name.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="absolute top-3 left-3 flex items-center gap-1.5">
                        <span className="tag bg-black/60 text-white backdrop-blur-md text-[10px] font-bold">
                          {p.leasing_type || "LTR"}
                        </span>
                        <span
                          className={`tag text-[10px] font-bold backdrop-blur-md ${
                            p.status === "Overdue"
                              ? "bg-red-500/80 text-white animate-pulse"
                              : p.status === "Active"
                              ? "bg-emerald-500/80 text-white"
                              : "bg-amber-500/80 text-white"
                          }`}
                        >
                          {p.status}
                        </span>
                      </div>
                    </div>

                    {/* Body Content */}
                    <div className="p-5 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="font-bold text-[18px] text-[var(--text)] truncate group-hover:text-teal-400 transition-colors">
                            {p.name}
                          </h4>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/properties/${p.id}`); }}
                            className="text-xs text-teal-400 hover:text-teal-300 font-bold shrink-0 flex items-center gap-1"
                          >
                            <span>View</span>
                            <ArrowRight size={12} />
                          </button>
                        </div>
                        <p className="text-[13px] text-[var(--muted)] truncate mt-0.5">
                          {[p.address, p.city, p.state].filter(Boolean).join(", ")}
                        </p>

                        {/* 2x2 Mini Metrics Grid */}
                        <div className="grid grid-cols-2 gap-4 mt-4 p-3.5 rounded-xl bg-[var(--surface-hover)]/60 border border-[var(--border)] text-xs">
                          <div>
                            <div className="text-[10px] text-[var(--muted)] font-semibold uppercase">MTD NOI</div>
                            <div className={`text-[18px] font-black tabular-nums mt-0.5 ${p.noiMtd >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                              {formatCurrency(p.noiMtd)}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] text-[var(--muted)] font-semibold uppercase">
                              {p.leasing_type === "STR" ? "Occupancy" : "Rent Status"}
                            </div>
                            <div className={`text-[18px] font-black mt-0.5 ${p.status === "Overdue" ? "text-red-400" : "text-teal-400"}`}>
                              {p.leasing_type === "STR" ? `${p.occupancyRate}%` : p.status}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] text-[var(--muted)] font-semibold uppercase">YTD Income</div>
                            <div className="text-[18px] font-bold tabular-nums text-[var(--text)] mt-0.5">
                              {formatCurrency(p.incomeYtd)}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] text-[var(--muted)] font-semibold uppercase">YTD Expenses</div>
                            <div className="text-[18px] font-bold tabular-nums text-[var(--text-secondary)] mt-0.5">
                              {formatCurrency(p.expenseYtd)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Rent Collection / Occupancy Progress Bar */}
                      <div className="mt-4 pt-3 border-t border-[var(--border)]">
                        <div className="flex items-center justify-between text-[11px] text-[var(--muted)] mb-1">
                          <span>{p.leasing_type === "STR" ? "Occupancy" : "Rent Collection"}</span>
                          <span className="font-bold text-[var(--text)]">
                            {p.leasing_type === "STR" ? `${p.occupancyRate}%` : (p.status === "Overdue" ? "0%" : "100%")}
                          </span>
                        </div>
                        <div className="w-full bg-[var(--surface-hover)] h-1.5 rounded-full overflow-hidden border border-[var(--border)]">
                          <div
                            className={`h-full rounded-full ${
                              p.status === "Overdue" ? "bg-red-400" : "bg-teal-500"
                            }`}
                            style={{
                              width: `${p.leasing_type === "STR" ? p.occupancyRate : (p.status === "Overdue" ? 10 : 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ================= DUE THIS WEEK & RECENT ACTIVITY (Fix 4: Equal Height, Align-Stretch) ================= */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch mb-10">
            {/* Left Column: Due This Week */}
            <div className="card p-5 sm:p-7 min-h-[400px] h-full flex flex-col justify-between self-stretch" data-testid="section-due-this-week">
              <div className="flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-[20px] font-bold text-[var(--text)]">Due This Week</h3>
                    <span className="text-xs px-2.5 py-0.5 rounded-full bg-teal-500/15 text-teal-400 font-bold">
                      {upcomingBills.length}
                    </span>
                  </div>
                  {upcomingBills.length > 0 && can.edit && (
                    <button
                      onClick={handleApproveAll}
                      disabled={approvingAll}
                      className="btn-primary text-xs py-1 px-3"
                      data-testid="approve-all-recurring"
                    >
                      <CheckCheck size={14} />
                      <span>{approvingAll ? "Approving…" : "Approve All"}</span>
                    </button>
                  )}
                </div>

                {upcomingBills.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center my-auto py-10">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mb-3">
                      <CheckCircle2 size={28} />
                    </div>
                    <div className="font-bold text-base text-[var(--text)]">All caught up!</div>
                    <div className="text-xs text-[var(--muted)] mt-1">
                      No recurring bills or mortgages scheduled for this week.
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {upcomingBills.map((item) => (
                      <div
                        key={item.id}
                        className="p-3.5 rounded-2xl bg-[var(--surface-hover)]/60 border border-[var(--border)] flex items-center justify-between gap-3 hover:border-teal-500/30 transition-colors"
                      >
                        <div className="min-w-0">
                          <div className="font-bold text-sm text-[var(--text)] truncate">
                            {item.category}
                          </div>
                          <div className="text-xs text-[var(--muted)] flex items-center gap-2 mt-0.5">
                            <span>{item.property_name || "Portfolio-wide"}</span>
                            <span>·</span>
                            <span>Due {item.next_due_date || "Soon"}</span>
                          </div>
                          <div className="text-sm font-black tabular-nums text-teal-400 mt-1">
                            ${Number(item.amount).toFixed(2)}
                          </div>
                        </div>

                        {can.edit && (
                          <button
                            onClick={() => handleApproveRecurring(item)}
                            className="btn-secondary text-xs py-1.5 px-3 shrink-0"
                            title="Post transaction to books now"
                          >
                            <CheckCircle2 size={13} className="text-teal-400" />
                            <span>Approve</span>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bottom pinned link */}
              <div className="mt-auto pt-4 border-t border-[var(--border)]">
                <button
                  onClick={() => navigate("/recurring")}
                  className="text-xs font-semibold text-teal-400 hover:text-teal-300 flex items-center gap-1"
                >
                  <span>Manage recurring rules</span>
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>

            {/* Right Column: Recent Activity Feed */}
            <div className="card p-5 sm:p-7 min-h-[400px] h-full flex flex-col justify-between self-stretch" data-testid="section-recent-activity">
              <div className="flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[20px] font-bold text-[var(--text)]">Recent Activity</h3>
                  <button
                    onClick={() => navigate("/transactions")}
                    className="text-xs font-semibold text-teal-400 hover:text-teal-300"
                    data-testid="view-all-transactions-link"
                  >
                    View All Transactions →
                  </button>
                </div>

                <div className="divide-y divide-[var(--border)]">
                  {recent.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center my-auto py-12 text-xs text-[var(--muted)]">
                      No transactions recorded yet.
                    </div>
                  ) : (
                    recent.slice(0, 8).map((tx) => {
                      const iconInfo = getCategoryIconInfo(tx.category, tx.type);
                      const IconComp = iconInfo.icon;
                      return (
                        <div
                          key={tx.id}
                          onClick={() => setSelectedTx(tx)}
                          className="min-h-[64px] py-3 flex items-center justify-between gap-3 hover:bg-[var(--surface-hover)] px-2 rounded-xl transition-colors cursor-pointer group"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedTx(tx);
                            }
                          }}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${iconInfo.bg}`}>
                              <IconComp size={18} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-bold text-[var(--text)] group-hover:text-teal-400 transition-colors truncate">
                                  {tx.category}
                                </span>
                                {tx.receipt_path && (
                                  <span className="text-[10px] text-teal-400 bg-teal-500/10 border border-teal-500/30 px-1.5 py-0.5 rounded flex items-center gap-1 font-semibold shrink-0">
                                    <Paperclip size={10} /> Receipt
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-[var(--muted)] truncate mt-0.5">
                                {[tx.property_name, tx.vendor_name || tx.tenant_name].filter(Boolean).join(" · ") || "—"}
                              </div>
                            </div>
                          </div>

                          <div className="text-right shrink-0">
                            <div
                              className={`font-black tracking-tight tabular-nums text-[16px] ${
                                tx.type === "Income" ? "text-emerald-400" : "text-rose-400"
                              }`}
                            >
                              {tx.type === "Income" ? "+" : "−"}{formatCurrency(tx.amount)}
                            </div>
                            <div className="text-[11px] text-[var(--muted)] mt-0.5">{formatDate(tx.date)}</div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Bottom pinned link */}
              <div className="mt-auto pt-4 border-t border-[var(--border)]">
                <button
                  onClick={() => navigate("/transactions")}
                  className="w-full text-center text-xs font-semibold text-teal-400 hover:text-teal-300 py-1"
                >
                  View complete general ledger →
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ================= QUICK ACTION FAB (MOBILE ONLY < 768px) ================= */}
      <div className="md:hidden fixed bottom-20 right-5 z-40">
        {fabOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30"
              onClick={() => setFabOpen(false)}
            />
            <div className="absolute bottom-16 right-0 z-40 flex flex-col items-end gap-2.5 pb-2 animate-in fade-in slide-in-from-bottom-5 duration-200">
              <button
                onClick={() => { setFabOpen(false); navigate("/transactions?new=1"); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[var(--surface)] text-[var(--text)] border border-[var(--border)] shadow-xl text-xs font-bold"
              >
                <span>Add Transaction</span>
                <div className="w-7 h-7 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
                  <Plus size={15} />
                </div>
              </button>

              <button
                onClick={() => { setFabOpen(false); navigate("/scan"); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[var(--surface)] text-[var(--text)] border border-[var(--border)] shadow-xl text-xs font-bold"
              >
                <span>Scan Receipt</span>
                <div className="w-7 h-7 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
                  <ScanLine size={15} />
                </div>
              </button>

              <button
                onClick={() => { setFabOpen(false); navigate("/mileage"); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[var(--surface)] text-[var(--text)] border border-[var(--border)] shadow-xl text-xs font-bold"
              >
                <span>Log Miles</span>
                <div className="w-7 h-7 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
                  <Car size={15} />
                </div>
              </button>

              <button
                onClick={() => { setFabOpen(false); navigate("/properties"); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[var(--surface)] text-[var(--text)] border border-[var(--border)] shadow-xl text-xs font-bold"
              >
                <span>Add Booking</span>
                <div className="w-7 h-7 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
                  <Calendar size={15} />
                </div>
              </button>

              <button
                onClick={() => { setFabOpen(false); navigate("/properties?new=1"); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[var(--surface)] text-[var(--text)] border border-[var(--border)] shadow-xl text-xs font-bold"
              >
                <span>New Property</span>
                <div className="w-7 h-7 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
                  <Building2 size={15} />
                </div>
              </button>
            </div>
          </>
        )}

        {/* Primary FAB Button */}
        <button
          onClick={() => setFabOpen((prev) => !prev)}
          className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] text-white flex items-center justify-center shadow-2xl shadow-teal-500/40 border border-teal-500/40 active:scale-95 transition-transform"
          data-testid="mobile-quick-action-fab"
          aria-label="Quick Actions"
        >
          <Plus size={26} className={`transition-transform duration-200 ${fabOpen ? "rotate-45" : ""}`} />
        </button>
      </div>

      {/* Transaction Detail Modal */}
      {selectedTx && (
        <TransactionDetailModal
          transaction={selectedTx}
          onClose={() => setSelectedTx(null)}
          onUpdate={(updated) => {
            setRecent((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            setSelectedTx(updated);
          }}
        />
      )}
    </Layout>
  );
}
